import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { AuthLoadingState } from '@/components/auth/AuthLoadingState';
import { useEnhancedToast } from '@/components/enhanced-toast';
import { useEffect, useRef } from 'react';

interface SuperAdminRouteProps {
  children: React.ReactNode;
  fallbackPath?: string;
}

export const SuperAdminRoute = ({ 
  children, 
  fallbackPath = '/dashboard' 
}: SuperAdminRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { loading: permissionsLoading } = usePermissions();
  const location = useLocation();
  const { showToast } = useEnhancedToast();
  const hasShownDemoToast = useRef(false);

  // Show toast for demo users trying to access admin routes
  useEffect(() => {
    if (user?.isDemo && !hasShownDemoToast.current) {
      hasShownDemoToast.current = true;
      showToast({
        title: "Demo Restriction",
        description: "Admin panel is not available in demo mode. Sign up for full access!",
        variant: "warning",
      });
    }
  }, [user?.isDemo, showToast]);

  if (authLoading || permissionsLoading) {
    return <AuthLoadingState />;
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Block demo users from admin routes
  if (user.isDemo) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check super admin status directly from profile
  if (!user.profile?.is_super_admin) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};
